import React, { useCallback, useEffect, useRef, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';
import type { ResolvedPos, Slice } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import { Popover, PopoverBody } from 'reactstrap';

import { BYTES_IN_MB, MAX_UPLOAD_SIZE_MB, RICH_TEXT_IMAGE_WIDTH_LADDER } from '~constants/common';

import { useToast } from '~libs/toast';

import FileUpload from '~components/FileUpload';
import Icon from '~components/icon';
import {
  faBold,
  faGripLines,
  faGripLinesVertical,
  faHeading,
  faImage,
  faItalic,
  faListOl,
  faListUl,
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
  faMinus,
  faPlus,
  faSpinner,
  faStrikethrough,
  faTableCells,
  faTrash,
  faUnderline,
} from '~components/icon';

import { useUploadFormImageMutation } from '~forms/actions/formImages';

import MergeTokenHighlight from './mergeTokenHighlight';
import { prepareFormImage, PreparedImage } from './prepareFormImage';
import { RichTextImage } from './RichTextImage';
import RichTextToolbarButton from './RichTextToolbarButton';

interface ComponentProps {
  value: string; // HTML
  onChange: (html: string) => void;
  ariaLabel?: string;
}

// StarterKit brings bold, italic, strike, headings, bullet/ordered lists, history. Underline is the one
// core mark it omits, so it's added explicitly. MergeTokenHighlight chips pasted {{merge:}} sentinels.
// Tables (TAV-3154) are basic only: no images or links, and no cell merging, per-cell styling or
// column resizing. `resizable: false` keeps every serialised cell free of a colwidth attribute, so the
// stored HTML survives the sanitiser's empty attribute allow-list and still re-parses into the same table.
// RichTextImage (TAV-3155) is the S3-backed reference image node. See its own docblock.
const EXTENSIONS = [
  StarterKit,
  Underline,
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  RichTextImage,
  MergeTokenHighlight,
];

const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];
// The width a freshly-inserted image starts at, the middle rung of the resize ladder.
const DEFAULT_IMAGE_WIDTH = RICH_TEXT_IMAGE_WIDTH_LADDER[2];

// Matches a pasted <img> that does not carry our own reference attribute, a hotlinked or
// otherwise foreign image. The schema's parseHTML rule (img[data-form-image]) already refuses to
// turn it into a node; this only decides whether to show the informational toast (AC8).
const FOREIGN_IMG = /<img\b(?![^>]*\bdata-form-image=)[^>]*>/i;

// The table nodes an image must never end up inside. The PDF renderer skips an image token while it
// is capturing a table (document-engine `richTextToPdf.ts`), so an image authored in a cell renders
// on the canvas, in the preview, in the fill view and in the frozen record, and is silently absent
// from the export. That is the screen/PDF divergence AC6 rules out.
const TABLE_NODE_NAMES = ['table', 'tableRow', 'tableCell', 'tableHeader'];

const TABLE_IMAGE_MESSAGE = 'Images cannot be placed inside a table. Move the cursor outside the table and try again.';

// Every insert path resolves to a document position eventually, so an ancestor walk is the one check
// the drop handler, the paste handler and the post-upload insert can all share. The toolbar keeps its
// own `editor.isActive('table')` gate, which hides the button rather than rejecting an insert.
const isInsideTable = ($pos: ResolvedPos): boolean => {
  const { depth } = $pos;
  for (let level = depth; level > 0; level -= 1) {
    const { name } = $pos.node(level).type;
    if (TABLE_NODE_NAMES.includes(name)) return true;
  }
  return false;
};

// True when a dragged ProseMirror slice carries an image. An internal drag of an existing image
// carries no `dataTransfer` file, only this slice, so it is the only thing the drop handler can
// read to tell an image move apart from any other content move.
const sliceHasImage = (slice?: Slice | null): boolean => {
  if (!slice) return false;
  let found = false;
  slice.content.descendants((node) => {
    if (node.type.name === 'image') found = true;
    return !found;
  });
  return found;
};

/**
 * Tiptap (ProseMirror) authoring surface for a rich_text block. Emits semantic HTML on every change,
 * which is what gets persisted and sanitised for the read-only filler view (RichTextView).
 */
const RichTextEditor: React.FC<ComponentProps> = ({ value, onChange, ariaLabel }) => {
  const { showToast } = useToast();
  const [uploadFormImage, { isLoading: isUploadingImage }] = useUploadFormImageMutation();
  const [imagePopoverOpen, setImagePopoverOpen] = useState(false);
  // The mutation's own isLoading covers the network call only. Preparation runs before it and does
  // the canvas decode, downscale and re-encode, which is the slow half for a large photo. Tracking
  // it inside uploadPreparedImage rather than in each caller means drop and paste get the same
  // feedback the button does.
  const [preparingImage, setPreparingImage] = useState(false);
  const insertImageButtonRef = useRef<HTMLSpanElement>(null);

  // Shared by every insert path (button, drop, paste): downscale/re-encode, then upload. Returns
  // the new row's id, or null when the file could not be decoded, when downscaling couldn't fit the
  // byte cap, or when the upload itself failed. All three are toasted here and none of them reject,
  // so callers only need to check for null.
  const uploadPreparedImage = useCallback(
    async (file: File): Promise<string | null> => {
      // All three insert paths funnel through here, so this is the one place the size rule belongs.
      // The button path is already gated by FileUpload's own `maxSizeMB` and never reaches this, but
      // drop and paste have no such gate and `prepareFormImage` below opens with `readAsDataURL`
      // plus a full bitmap decode. An unbounded file kills the tab inside that decode, which is the
      // one failure that cannot be toasted.
      if (file.size > MAX_UPLOAD_SIZE_MB * BYTES_IN_MB) {
        showToast('danger', `Image is larger than ${MAX_UPLOAD_SIZE_MB} MB. Try a smaller file.`);
        return null;
      }
      setPreparingImage(true);
      try {
        let prepared: PreparedImage | null;
        try {
          prepared = await prepareFormImage(file);
        } catch {
          // A file the browser cannot decode. FileUpload's MIME check reads the OS-reported type, so
          // a corrupt or mislabelled image reaches here looking perfectly valid.
          showToast('danger', 'Could not read this image. It may be corrupt or in an unsupported format.');
          return null;
        }
        if (!prepared) {
          showToast('danger', 'Image is too large to upload, even after resizing. Try a smaller image.');
          return null;
        }
        if (prepared.downscaled) {
          showToast('info', 'Image was resized to fit the form.');
        }
        const formData = new FormData();
        formData.append('file', prepared.file);
        try {
          const { id } = await uploadFormImage(formData).unwrap();
          return id;
        } catch {
          showToast('danger', 'Failed to upload image. Please try again.');
          return null;
        }
      } finally {
        // finally, not a call on each exit: five of the branches above return early, and a missed
        // one would strand the placeholder on screen for the rest of the session.
        setPreparingImage(false);
      }
    },
    [showToast, uploadFormImage],
  );

  // Drop/paste insert path. Takes `view` directly (from the ProseMirror event handler) rather than
  // the `editor` returned below, so this has no ordering dependency on the useEditor() call below it.
  const insertImageAt = useCallback(
    async (view: EditorView, file: File, pos: number) => {
      const imageId = await uploadPreparedImage(file);
      if (!imageId || view.isDestroyed) return;
      const node = view.state.schema.nodes.image.create({ imageId, width: DEFAULT_IMAGE_WIDTH });
      // `pos` was captured before the upload and the author is free to keep editing during it, so it
      // can point past the end of a document that has since shrunk. Clamp to the document as it is
      // now, and fall back to the cursor rather than throwing a RangeError into an unhandled
      // rejection, which would lose the uploaded image with nothing shown to explain why.
      const safePos = Math.min(pos, view.state.doc.content.size);
      // The handlers below reject a table position before uploading, but the document stays live for
      // the length of that upload, so a position that was outside a table when the drop landed can be
      // inside one by the time the node is dispatched. Re-check against the document as it is now.
      // Rejecting here leaves an uploaded object with no reference, which is the right trade against
      // authoring content the PDF will drop, and the pre-upload guards make it a rare race.
      if (isInsideTable(view.state.doc.resolve(safePos))) {
        showToast('info', TABLE_IMAGE_MESSAGE);
        return;
      }
      try {
        view.dispatch(view.state.tr.insert(safePos, node));
      } catch {
        // The cursor fallback needs the same guard. The author having moved into a cell mid-upload is
        // exactly the case where the primary insert above is most likely to have thrown.
        if (isInsideTable(view.state.selection.$from)) {
          showToast('info', TABLE_IMAGE_MESSAGE);
          return;
        }
        view.dispatch(view.state.tr.insert(view.state.selection.from, node));
      }
    },
    [uploadPreparedImage],
  );

  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value || '',
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: {
        class: 'rich-text-editable',
        'aria-label': ariaLabel ?? 'Rich text content',
        role: 'textbox',
        'aria-multiline': 'true',
      },
      handleDrop: (view, event) => {
        // `posAtCoords` returns a position inside a <td> whenever the drop lands over a cell, so the
        // toolbar's isActive gate does not cover either path below.
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? view.state.selection.from;
        const intoTable = isInsideTable(view.state.doc.resolve(pos));

        const file = event.dataTransfer?.files?.[0];
        if (!file || !ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) {
          // Moving an image that is already in the document. `draggable: false` on RichTextImage
          // takes the affordance away, but ProseMirror hands it back the moment the image is
          // selected (NodeViewDesc.selectNode sets draggable on a non-draggable selected node), and
          // the dragstart handler then drags the selection rather than consulting the spec. So this
          // is the check that actually closes the path; the spec flag only makes it harder to reach.
          if (intoTable && sliceHasImage(view.dragging?.slice)) {
            showToast('info', TABLE_IMAGE_MESSAGE);
            return true;
          }
          return false;
        }
        event.preventDefault();
        if (intoTable) {
          showToast('info', TABLE_IMAGE_MESSAGE);
          return true;
        }
        insertImageAt(view, file, pos);
        return true;
      },
      handlePaste: (view, event) => {
        const file = event.clipboardData?.files?.[0];
        if (!file || !ALLOWED_IMAGE_MIME_TYPES.includes(file.type)) return false;
        event.preventDefault();
        // The caret is inside the cell whenever the author is typing in one, so paste reaches a table
        // position just as readily as drop does.
        if (isInsideTable(view.state.selection.$from)) {
          showToast('info', TABLE_IMAGE_MESSAGE);
          return true;
        }
        insertImageAt(view, file, view.state.selection.from);
        return true;
      },
      // Enforcement of AC8 lives in the schema (RichTextImage's parseHTML requires the reference
      // attribute, so a foreign <img> never becomes a node). This only supplies the "predictably
      // rather than silently" signal.
      transformPastedHTML: (html) => {
        if (FOREIGN_IMG.test(html)) {
          showToast('info', 'Images pasted from another site were not added. Use the image button to upload them.');
        }
        return html;
      },
    },
  });

  // Keep the editor in sync if `value` is replaced from outside (e.g. a different field is loaded into
  // this instance). The guard prevents a feedback loop: after onUpdate, getHTML() already equals value.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  // Button insert path. Needs `editor` (declared above), so this is defined after it rather than
  // alongside uploadPreparedImage/insertImageAt.
  const handleFileSelected = useCallback(
    async (file: File) => {
      setImagePopoverOpen(false);
      const imageId = await uploadPreparedImage(file);
      if (!imageId || !editor) return;
      editor
        .chain()
        .focus()
        .insertContent({ type: 'image', attrs: { imageId, width: DEFAULT_IMAGE_WIDTH } })
        .run();
    },
    [editor, uploadPreparedImage],
  );

  // Covers the whole insert: canvas preparation and the upload that follows it.
  const isInsertingImage = preparingImage || isUploadingImage;

  if (!editor) return null;

  // Resize reads the current image's width back out of ladder, so Larger/Smaller step exactly one
  // rung. Not found (-1) disables both. The only way to reach it is a pre-TAV-3155 stored image,
  // which cannot exist, but it's a safe fallback rather than a crash if it ever does.
  const imageWidthIndex = RICH_TEXT_IMAGE_WIDTH_LADDER.indexOf(Number(editor.getAttributes('image').width));
  const resizeImage = (direction: 1 | -1) => {
    const nextIndex = imageWidthIndex + direction;
    if (nextIndex < 0 || nextIndex >= RICH_TEXT_IMAGE_WIDTH_LADDER.length) return;
    editor.chain().focus().updateAttributes('image', { width: RICH_TEXT_IMAGE_WIDTH_LADDER[nextIndex] }).run();
  };

  return (
    <div className="rich-text-editor rich-text-editor--tiptap" data-cy="rich-text-editor-tiptap">
      <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
        <RichTextToolbarButton
          icon={faBold}
          label="Bold"
          isActive={editor.isActive('bold')}
          onActivate={() => editor.chain().focus().toggleBold().run()}
        />
        <RichTextToolbarButton
          icon={faItalic}
          label="Italic"
          isActive={editor.isActive('italic')}
          onActivate={() => editor.chain().focus().toggleItalic().run()}
        />
        <RichTextToolbarButton
          icon={faUnderline}
          label="Underline"
          isActive={editor.isActive('underline')}
          onActivate={() => editor.chain().focus().toggleUnderline().run()}
        />
        <RichTextToolbarButton
          icon={faStrikethrough}
          label="Strikethrough"
          isActive={editor.isActive('strike')}
          onActivate={() => editor.chain().focus().toggleStrike().run()}
        />
        <span className="rich-text-toolbar-divider" aria-hidden="true" />
        <RichTextToolbarButton
          icon={faHeading}
          label="Heading (large)"
          isActive={editor.isActive('heading', { level: 1 })}
          onActivate={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        />
        <RichTextToolbarButton
          icon={faHeading}
          label="Heading (small)"
          isActive={editor.isActive('heading', { level: 2 })}
          onActivate={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <span className="rich-text-toolbar-divider" aria-hidden="true" />
        <RichTextToolbarButton
          icon={faListUl}
          label="Bullet list"
          isActive={editor.isActive('bulletList')}
          onActivate={() => editor.chain().focus().toggleBulletList().run()}
        />
        <RichTextToolbarButton
          icon={faListOl}
          label="Numbered list"
          isActive={editor.isActive('orderedList')}
          onActivate={() => editor.chain().focus().toggleOrderedList().run()}
        />
        <span className="rich-text-toolbar-divider" aria-hidden="true" />
        <RichTextToolbarButton
          icon={faTableCells}
          label="Insert table"
          onActivate={() => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()}
        />
        {/* Row/column controls are meaningless outside a table, and five permanently disabled
            buttons would double the toolbar for every form that has no table. isActive('table')
            re-evaluates on selection change, the same mechanism the toggles above already use. */}
        {editor.isActive('table') && (
          <>
            <span className="rich-text-toolbar-divider" aria-hidden="true" />
            {/* The axis glyph is paired with a plus or a minus beside it, because FontAwesome's
                free set has no directional plus (table-cells-row-plus is Pro only) and a bare
                plus on both Add buttons would leave row and column distinguishable only by
                tooltip. Deliberately side by side, not layered: a minus laid over faGripLines
                is invisible, since both are horizontal lines. */}
            <RichTextToolbarButton
              icon={faGripLines}
              pairedIcon={faPlus}
              label="Add row"
              onActivate={() => editor.chain().focus().addRowAfter().run()}
            />
            <RichTextToolbarButton
              icon={faGripLines}
              pairedIcon={faMinus}
              label="Delete row"
              onActivate={() => editor.chain().focus().deleteRow().run()}
            />
            <RichTextToolbarButton
              icon={faGripLinesVertical}
              pairedIcon={faPlus}
              label="Add column"
              onActivate={() => editor.chain().focus().addColumnAfter().run()}
            />
            <RichTextToolbarButton
              icon={faGripLinesVertical}
              pairedIcon={faMinus}
              label="Delete column"
              onActivate={() => editor.chain().focus().deleteColumn().run()}
            />
            <RichTextToolbarButton
              icon={faTrash}
              label="Delete table"
              onActivate={() => editor.chain().focus().deleteTable().run()}
            />
          </>
        )}
        {/* Hidden while the caret is in a table: the PDF renderer cannot lay an image out inside a
            cell, so hiding the button keeps the screen and the PDF in agreement rather than letting
            the builder author content that silently differs between the two. */}
        {!editor.isActive('table') && (
          <>
            <span className="rich-text-toolbar-divider" aria-hidden="true" />
            <span ref={insertImageButtonRef} className="rich-text-toolbar-image-btn">
              <RichTextToolbarButton
                icon={faImage}
                label="Insert image"
                disabled={isInsertingImage}
                onActivate={() => setImagePopoverOpen((open) => !open)}
              />
            </span>
            <Popover
              isOpen={imagePopoverOpen}
              target={insertImageButtonRef}
              placement="bottom-start"
              toggle={() => setImagePopoverOpen(false)}
              className="rich-text-image-popover"
            >
              <PopoverBody>
                <FileUpload
                  allowedExtensions={['.png', '.jpg', '.jpeg']}
                  allowedMimeTypes={ALLOWED_IMAGE_MIME_TYPES}
                  maxSizeMB={MAX_UPLOAD_SIZE_MB}
                  label="Drop an image here"
                  className="rich-text-image-upload"
                  onChange={handleFileSelected}
                />
              </PopoverBody>
            </Popover>
          </>
        )}
        {/* Resize/remove controls for the currently-selected image only, mirroring the table
            controls' isActive-gated pattern above. */}
        {editor.isActive('image') && (
          <>
            <span className="rich-text-toolbar-divider" aria-hidden="true" />
            <RichTextToolbarButton
              icon={faMagnifyingGlassMinus}
              label="Make image smaller"
              disabled={imageWidthIndex <= 0}
              onActivate={() => resizeImage(-1)}
            />
            <RichTextToolbarButton
              icon={faMagnifyingGlassPlus}
              label="Make image larger"
              disabled={imageWidthIndex === -1 || imageWidthIndex >= RICH_TEXT_IMAGE_WIDTH_LADDER.length - 1}
              onActivate={() => resizeImage(1)}
            />
            {/* deleteSelection(), not deleteNode('image'): Tiptap's deleteNode only walks the
                selection's ANCESTORS (`for (depth = $pos.depth; depth > 0; depth -= 1)`), and this
                image is a top-level block, so it is never an ancestor of anything and the command
                is a silent no-op. Selecting the image produces a NodeSelection spanning exactly it,
                which is what deleteSelection removes. */}
            <RichTextToolbarButton
              icon={faTrash}
              label="Remove image"
              onActivate={() => editor.chain().focus().deleteSelection().run()}
            />
          </>
        )}
      </div>
      <EditorContent editor={editor} />
      {/* Stands in for the image that is about to appear, sized like the resolved-image placeholder
          so the editor does not jump when the real one lands. The popover closes as soon as a file
          is chosen, so without this the builder gets no signal at all while a large photo is being
          decoded and re-encoded. */}
      {isInsertingImage && (
        <div className="rich-text-image-uploading" role="status">
          <Icon icon={faSpinner} spin aria-hidden="true" />
          <span>Adding image</span>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
